<template>
  <div class="connection-card-wrap">
    <div v-if="status === 'open'" class="connection-card connection-card--open">
      <div class="connection-card-userinfo">
        <div class="connection-card__avatar">
          <font-awesome-icon icon="fa-solid fa-user-plus" />
        </div>
        <div class="connection-card__info">
          <div class="connection-card__info--name">{{ $gettext('Open team slot') }}</div>
          <span class="connection-card__info--email">{{ $gettext('Click here to invite a user') }}</span>
        </div>
      </div>
      <div class="connection-card__working-at">
        <div style="font-size: 14px;">
          <p class="empty-text"><span v-translate>This is an empty slot that you've already paid. If unused, will not be billed for next period.</span></p>
        </div>
      </div>
    </div>
    <div v-else class="connection-card" :class="cardClasses" :style="cardCssVars">
      <div class="connection-card-userinfo">
        <div class="connection-card__avatar">
          <font-awesome-icon v-if="status==='pending'" icon="fa-solid fa-user-clock" />
          <Avatar v-else :user-id="connection.id" tooltip-disabled />
        </div>

        <div class="connection-card__info" v-if="status === 'pending'">
          <div class="connection-card__info--name">{{ connection.invite_to_name }}</div>
          <span class="connection-card__info--email">{{ connection.invite_to }}</span>
          <div class="connection-card__info--data">
            <div class="connection-item">
              <font-awesome-icon icon="fa-solid fa-arrow-right-arrow-left" /> <span class="ml-1">{{ connection.company_profile.name }}</span>
            </div>
          </div>
        </div>

        <div v-else class="connection-card__info">
          <div class="connection-card__info--name">{{ connection.display_name }}</div>
          <span class="connection-card__info--email">{{ connection.email }}</span>
        </div>
      </div>
      <div v-if="status !== 'pending'" class="connection-card__working-at">
        <div class="connection-card__working-at--title">{{ $gettext('Working at') }}</div>
        <ul class="connection-card__working-at--list">
          <template v-for="conn in connection.connections">
            <li v-if="itsMe || (!itsMe && isMyCompany(conn.company_profile))" :key="conn.id" class="connection-item">
              <span>{{ conn.company_profile.name }}</span>
              <span v-if="conn.rights.admin">
                <font-awesome-icon :id="'tooltip-working-at-' + conn.id" icon="fa-solid fa-sitemap" class="text-brand ml-2" />
                <b-tooltip :target="'tooltip-working-at-' + conn.id" triggers="hover" boundary="viewport">{{ $gettext('Admin') }}</b-tooltip>
              </span>
              <span v-if="!conn.is_active">
                <font-awesome-icon :id="'tooltip-working-at-' + conn.id" icon="fa-solid fa-warning" class="text-warning ml-2" />
                <b-tooltip :target="'tooltip-working-at-' + conn.id" triggers="hover" boundary="viewport">{{ $gettext('The connection with this company is inactive') }}</b-tooltip>
              </span>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import Avatar from "@/components/Avatar"

export default {
  name: 'UserCard',
  components: {
    Avatar,
  },
  props: {
    connection: {
      type: Object,
      default: () => {}
    },
    status: {
      type: String,
      default: 'active'
    },
  },
  computed: {
    ...mapGetters({ 
      currentUser: 'main/getCurrentUser',
      availableCompanies: "team/getMyOwnCompanies"
    }),
    cardClasses () {
      let cssClasses = ''
      if (this.status === 'inactive') {
        cssClasses += 'connection-card--inactive gk-tip gk-tip--team-inactive'
      }
      if (this.status === 'pending') {
        cssClasses += 'connection-card--pending gk-tip gk-tip--team-pending'
      }
      cssClasses += this.itsMe ? 'itsme' : ''
      return cssClasses
    },
    itsMe () {
      return this.currentUser.id === this.connection.id
    },
    cardCssVars () {
      let cssVars = ''
      if (this.status === 'inactive') {
        cssVars += `--inactivetiptext: '${ this.$gettext('inactive') }'`
      }
      if (this.status === 'pending') {
        cssVars += `--pendingtiptext: '${ this.$gettext('invite pending') }'`
      }
      return cssVars
    }
  },
  methods: {
    isMyCompany (company_profile) {
      return this.availableCompanies.find(company => company.id === company_profile.id)
    }
  },
}
</script>

<style lang="scss">
  .connection-card {
    $avatarSize: 60px;

    max-height: 200px;
    height: 200px;

    border: 1px solid #d1d1d1;
    border-radius: $radiusLg;
    padding: 0.5rem !important;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    position: relative;
    align-items: center;
    overflow: hidden;

    .connection-card-userinfo {
      display: flex;
      align-items: center;
      gap: 1rem;
      width: 100%;
    }

    &--pending {
      .connection-card__avatar {
        background: #feebd5;
        color: #f9ae40;
      }
    }
    &--inactive {
      opacity: 0.6;
      .connection-card__avatar {
        filter: grayscale(1);
      }
    }
    &--open {
      cursor: pointer;
      background: white;
      border-color: #f2f2f2;
      color: #333;
      transition: all 0.2s ease;
      &:hover {
        border-color: #d1d1d1;
        .connection-card__avatar {
          background: $colorBrand;
        }
      }
      .connection-card__avatar {
        background: #9fd5c4;
        color: white;
        transition: background-color 0.2s ease;
      }
    }
    &__avatar {
      width: $avatarSize;
      height: $avatarSize;
      flex-shrink: 0;
      background: #ebebeb;
      border-radius: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      color: white;
      overflow: hidden;

      .profile-badge {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .profile-badge, .avatar { width: 100%; height: 100%; }
      .avatar {
        border-radius: 100%;
        margin: 0; float: none;
      }

    }
    &__info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      flex: 1;
      height: 100%;
      &--name, &--email {
        overflow-x: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
      }
      &--name {
        font-size: 20px;
        font-family: 'DINNextLTPro-Medium';
      }
      &--email {
        font-size: 14px;
        opacity: 0.6;
      }
      &--data {
        padding-top: 0.5rem; 
        margin-top: 0.5rem; 
        border-top: 1px solid #f2f2f2;
        width: 100%;
      }
      &--actions {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        font-size: 14px;
      }
    }
    &__working-at {
      border-top: 1px solid #f2f2f2;
      width: 100%;
      height: 100%;
      overflow: hidden;
      padding-top: 0.5rem;
      &--title {
        color: $colorBrand;
        font-size: 14px;
        height: 1.5rem;;
      }
      &--list {
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 14px;
        height: calc(100% - 1.5rem);
        overflow-y: auto;
      }
      .svg-inline--fa {
        font-size: 12px;
        color: $colorBrand;
      }
    }
    .connection-item {
      display: flex;
      align-items: center;
      .svg-inline--fa {
        font-size: 12px;
        color: $colorBrand;
        margin-right: 0.25rem;
        padding-top: 2px;
      }
    }
  }
</style>